import { CHECKLIST_ITEMS } from './onboarding.constants';
import type { TourModule } from './onboarding.constants';
import type { TourStatus, ChecklistStatusData } from './tour.interfaces';

/** Ítems del checklist que dependen de un tour (el tour es la "puerta"). */
export const checklistToTourMap: Record<string, TourModule> = {
  employees: 'employees',
  services: 'services',
  products: 'products',
  firstSale: 'pos',
};

/** Subconjunto mínimo de un certificado/RUC que devuelve GET /billing/certificates. */
export interface BillingRucLike {
  status?: number;
  expirationDate?: string | null;
  channel?: string | null;
  pseStatus?: number | null;
  isActive?: number | boolean;
}

const CERT_ACTIVE = 1; // CertificateStatus.ACTIVE
const PSE_ACTIVE = 3; // PseStatus.ACTIVE

/**
 * La facturación electrónica cuenta como lista SOLO si hay un RUC activo para
 * emitir: certificado digital OSE sincronizado y vigente, o enrolamiento PSE activo.
 */
export const hasActiveEmissionRuc = (rucs: BillingRucLike[] | null | undefined, now = new Date()): boolean =>
  (rucs ?? []).some((r) => {
    if (r.isActive !== undefined && !Number(r.isActive)) return false;
    if (r.channel === 'PSE') return Number(r.pseStatus) === PSE_ACTIVE;
    if (Number(r.status) !== CERT_ACTIVE) return false;
    if (r.expirationDate) {
      const exp = new Date(r.expirationDate);
      if (!Number.isNaN(exp.getTime()) && exp.getTime() < now.getTime()) return false;
    }
    return true;
  });

export interface ChecklistContext {
  checklist: Record<string, boolean>;
  checklistStatus: ChecklistStatusData | null;
  tours: Record<string, TourStatus>;
  firstSale: boolean;
  hasBillingRuc: boolean;
}

/** Única fuente de verdad: ¿este ítem del checklist está completado? */
export const isChecklistItemDone = (key: string, ctx: ChecklistContext): boolean => {
  if (key === 'billing') return ctx.hasBillingRuc;
  // Si el tour sigue pendiente, el ítem NO está completado (el tour es la puerta).
  const tourModule = checklistToTourMap[key];
  if (tourModule && ctx.tours[tourModule] === 'pending') return false;
  if (key === 'firstSale') return ctx.firstSale || !!ctx.checklistStatus?.hasFirstSale;
  return true; // tour completado u omitido
};

/** % = completadas / total de ítems * 100, con tope 100. */
export const calculateCompletionPct = (ctx: ChecklistContext): number => {
  const total = CHECKLIST_ITEMS.length;
  if (!total) return 0;
  const done = CHECKLIST_ITEMS.filter((i) => isChecklistItemDone(i.key, ctx)).length;
  return Math.min(100, Math.round((done / total) * 100));
};
